import { Button, Group, Modal, TextInput, Switch, Table, Stack , Select, Paper} from "@mantine/core";
import { IconSearch, IconX } from '@tabler/icons-react';
import { useForm } from "@mantine/form"
import { useEffect, useState } from "react";
import { ApplicationAddressSystemType, AddUpdateApplicationAddressSystemType, ContractUserApplicationAddressSystemType } from "../../../../types/ApplicationAddressSystem";
import { addUpdateAddressSystem, getContractUsersForAddressById } from "../../../../services/UserService";
import { notifications } from "@mantine/notifications";
import { UserType } from "../../../../types/User";
import styles from './ModalForms.module.css';
import { useTranslation } from "../../../../hooks/useTranslation";
import useTransientStore from "../../../../data/transcientStore";

interface AddAddressFormProps {
    opened: boolean;
    open: () => void;
    close: () => void;
    onSuccess: () => void;
    allAddressSystems?: ApplicationAddressSystemType[];
    allUsers: UserType[]
}

interface EditAddressFormProps extends AddAddressFormProps {
    selectedAddress: ApplicationAddressSystemType
}

export function ViewUsersForm({ opened, open, close, addressSystemId }: { opened: boolean; open: () => void; close: () => void; addressSystemId: number }) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 346, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    const [users, setUsers] = useState<{ username: string }[]>([]);
    useEffect(() => {
        async function fetchUsers() {
            if (opened && addressSystemId) {
                try {
                    const data = await getContractUsersForAddressById(addressSystemId);
                    setUsers(data);
                } catch {
                    setUsers([]);
                }
            }
        }
        fetchUsers();
    }, [opened, addressSystemId]);

    return (
        <Modal opened={opened} onClose={close} title={getTranslation("TITLE_USERS") || "Users"} centered radius={'md'} size="xs">
            <div className={styles.formModal}>
                <Table className={styles.usersTable}>
                    <Table.Thead className={styles.stickyTableHeader}>
                        <Table.Th className={styles.headerCell}>
                            {getTranslation("LABEL_ADDRESS_USERS_USERNAME") || "Username"}
                        </Table.Th>
                    </Table.Thead>
                    <Table.Tbody>
                        {users.length <= 0 ? (
                            <Table.Tr>
                                <Table.Td>
                                    {getTranslation("LABEL_ADDRESS_USERS_NONE_FOUND") || "No users found."}
                                </Table.Td>
                            </Table.Tr>
                        ) : (
                            users.map((user, idx) => (
                                <Table.Tr key={idx} className={styles.noBorderRow}>
                                    <Table.Td>{user.username}</Table.Td>
                                </Table.Tr>
                            ))
                        )}
                    </Table.Tbody>
                </Table>
            </div>
        </Modal>
    );
}

export function AddAddressForm({ opened, open, close, onSuccess, allAddressSystems, allUsers }: AddAddressFormProps) {
    const { getTranslation } = useTranslation({ pageId: 346, addressSystemId: 0, autoLoad: true });
    const userLabels = allUsers.map((user) => user.username).sort((a, b) => a.localeCompare(b));;
    const form = useForm({
        mode: 'controlled',
        initialValues: {
            id: null,
            name: '',
            status: 0,
            userId: 0 // Assuming you want to add a user ID field
        },

        validate: {
            id: (value: null) => (value !== null ? (getTranslation("LABEL_ADDRESS_FORM_ID_EMPTY") || "ID must be empty.") : null),
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_ADDRESS_FORM_NAME_REQUIRED") || "Please enter an address system name.";
                if (allAddressSystems?.some(asn => asn.name?.toLowerCase().trim() === value.toLowerCase().trim())) {
                    return getTranslation("LABEL_ADDRESS_FORM_NAME_TAKEN") || "This address system name is already taken.";
                }
                return null;
            }
        }
    })

    const [addedUsers, setAddedUsers] = useState<ContractUserApplicationAddressSystemType[]>([]);
    const [selectKey, setSelectKey] = useState<number>(0);

    function handleUserChange(clickedUser: string) {
        const foundUser = allUsers.find((user) => user.username === clickedUser);
        const foundId = foundUser ? foundUser.id : 0;
        form.setFieldValue('userId', foundId);
    }

    return (
        <Modal opened={opened} onClose={() => { 
            form.reset(); 
            setAddedUsers([]); 
            setSelectKey(prev => prev + 1); 
            close(); 
        }} title={getTranslation("TITLE_ADD_ADDRESS_SYSTEM") || "Add Address System"} centered radius={'md'}>
            <form onSubmit={form.onSubmit(async (values) => {
                const payload: AddUpdateApplicationAddressSystemType = {
                    ...values,
                    users: addedUsers
                };
                await addUpdateAddressSystem(payload);
                form.reset();
                setAddedUsers([]); // Clear added users after submit
                close();
                onSuccess();
                notifications.show({
                    title: getTranslation("LABEL_ADDRESS_FORM_SUCCESS_TITLE") || "Success",
                    message: getTranslation("LABEL_ADDRESS_FORM_SUBMITTED") || "Address System Successfully Submitted.",
                    color: 'green',
                    withBorder: true,
                    withCloseButton: true,
                    autoClose: 5000,
                    className: 'notification-success'
                    // position: 'top-center'
                })
            })}>
                <TextInput
                    withAsterisk
                    label={getTranslation("LABEL_ADDRESS_SYSTEM_NAME") || "Address System Name"}
                    placeholder={getTranslation("PLACEHOLDER_ENTER_ADDRESS_SYSTEM_NAME") || "Enter Address System Name"}
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                />
                {/* User management section */}
                <Stack mt="md" className={styles.formStack}>
                    <Group align="end" gap="xs" className={styles.formGroup}>
                        <Select
                            key={selectKey}
                            // withAsterisk
                            label={getTranslation("LABEL_USERS") || "Users"}
                            placeholder={getTranslation("PLACEHOLDER_SELECT_A_USER") || "Select a user..."}
                            rightSection={<IconSearch size={18} color="#009DF0" />}
                            className={styles.nameInput}
                            data={userLabels}
                            value={form.values.userId === 0 ? '' : allUsers.find(u => u.id === form.values.userId)?.username || ''}
                            onChange={(value) => handleUserChange(value ?? '')}
                            error={form.errors.userId}
                            searchable
                        />
                        <Button
                            mt="md"
                            disabled={!form.values.userId || form.values.userId === 0}
                            onClick={(e) => {
                                e.preventDefault();
                                const user = allUsers.find(u => u.id === form.values.userId);
                                if (user && !addedUsers.some(u => u.id === user.id)) {
                                    setAddedUsers([...addedUsers, { id: user.id, username: user.username }]);
                                    form.setFieldValue('userId', 0); // Reset selection
                                    setSelectKey(prev => prev + 1); // Force Select to re-render and clear
                                }
                            }}>
                            {getTranslation("LABEL_ADDRESS_FORM_ADD_USER") || "Add"}
                        </Button>
                    </Group>
                    {addedUsers.length > 0 && (
                        <Paper p="sm" withBorder>
                            <div className={styles.scrollableTable}>
                                <Table highlightOnHover className={styles.userTable}>
                                    <Table.Thead className={styles.stickyHeader}>
                                        <Table.Tr>
                                            <Table.Th className={styles.headerCell}>
                                                {getTranslation("LABEL_ADDRESS_FORM_USERNAME") || "Username"}
                                            </Table.Th>
                                            <Table.Th className={styles.centerCell}>
                                                {getTranslation("LABEL_ADDRESS_FORM_REMOVE") || "Remove"}
                                            </Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {addedUsers
                                        .sort((a, b) => a.username.localeCompare(b.username))
                                        .map(user => (
                                            <Table.Tr key={user.id}>
                                                <Table.Td className={styles.noBorderCell}>{user.username}</Table.Td>
                                                <Table.Td className={styles.centerCell}>
                                                    <Button variant="subtle" color="red" size="xs" px={4} onClick={() => setAddedUsers(addedUsers.filter(u => u.id !== user.id))}>
                                                        <IconX size={18} />
                                                    </Button>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </div>
                        </Paper>
                    )}
                </Stack>

                <Group justify="flex-end" mt="md">
                    <Button type="submit">{getTranslation("LABEL_ADDRESS_FORM_SUBMIT") || "Submit"}</Button>
                </Group>
            </form>
        </Modal>
    )
}


export function EditAddressForm({ opened, open, close, selectedAddress, onSuccess, allAddressSystems, allUsers }: EditAddressFormProps) {
    const { getTranslation } = useTranslation({ pageId: 346, addressSystemId: selectedAddress?.id || 0, autoLoad: true });
    const userLabels = allUsers.map((user) => user.username).sort((a, b) => a.localeCompare(b));;
    const form = useForm({
        mode: 'controlled',
        initialValues: {
            id: 0,
            name: '',
            status: 0,
            userId: 0 // Assuming you want to add a user ID field
        },
        validate: {
            name: (value: string) => {
                if (!value || value.trim() === '') return getTranslation("LABEL_ADDRESS_FORM_NAME_REQUIRED") || "Please enter an address system name.";
                if (allAddressSystems?.some(asn => asn.name?.toLowerCase().trim() === value.toLowerCase().trim() && asn.id !== selectedAddress.id)) {
                    return getTranslation("LABEL_ADDRESS_FORM_NAME_TAKEN") || "This address system name is already taken.";
                }
                return null;
            }
        },
    });
    
    const [addedUsers, setAddedUsers] = useState<ContractUserApplicationAddressSystemType[]>([]);
    const [selectKey, setSelectKey] = useState<number>(0);

    function handleUserChange(clickedUser: string) {
        const foundUser = allUsers.find((user) => user.username === clickedUser);
        const foundId = foundUser ? foundUser.id : 0;
        form.setFieldValue('userId', foundId);
    }

    // Fetch contract users for the selected address system when opened
    useEffect(() => {
        async function fetchContractUsers() {
            if (opened && selectedAddress) {
                // Fetch contract users for this address system
                try {
                    const data = await getContractUsersForAddressById(selectedAddress.id);
                    setAddedUsers(data.map((u: any) => ({ id: u.id, username: u.username })));
                } catch {
                    setAddedUsers([]);
                }
            }
        }
        fetchContractUsers();
    }, [opened, selectedAddress]);

    useEffect(() => {
        if (opened && selectedAddress) {
            form.setValues({
                id: selectedAddress.id || 0,
                name: (selectedAddress.name || '').trim(), // Trim only leading/trailing spaces
                status: selectedAddress.status || 0
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, selectedAddress]); // form is stable from useForm

    return (
        <Modal
            opened={opened}
            onClose={() => {
                form.reset();
                setAddedUsers([]);
                close();
            }}
            title={getTranslation("TITLE_EDIT_ADDRESS_SYSTEM") || "Edit Address System"}
            centered
            radius={'md'}
        >
            <form
                onSubmit={form.onSubmit(async (values) => {
                    const payload: AddUpdateApplicationAddressSystemType = {
                        id: selectedAddress.id,
                        name: values.name,
                        status: values.status,
                        users: addedUsers
                    };
                    await addUpdateAddressSystem(payload);
                    form.reset();
                    setAddedUsers([]);
                    close();
                    onSuccess();
                    notifications.show({
                        title: getTranslation("LABEL_ADDRESS_FORM_SUCCESS_TITLE") || "Success",
                        message: getTranslation("LABEL_ADDRESS_FORM_UPDATED") || "Address System Successfully Updated.",
                        color: 'green',
                        withBorder: true,
                        withCloseButton: true,
                        autoClose: 5000,
                        className: 'notification-success'
                        // position: 'top-center',
                    });
                })}
            >
                <TextInput
                    withAsterisk
                    label={getTranslation("LABEL_ADDRESS_SYSTEM_NAME") || "Address System Name"}
                    placeholder={getTranslation("PLACEHOLDER_ENTER_ADDRESS_SYSTEM_NAME") || "Enter Address System Name"}
                    key={form.key('name')}
                    {...form.getInputProps('name')}
                />

                {/* User management section */}
                <Stack mt="md" className={styles.formStack}>
                    <Group align="end" gap="xs" className={styles.formGroup}>
                        <Select
                            key={selectKey}
                            // withAsterisk
                            label={getTranslation("LABEL_USERS") || "Users"}
                            placeholder={getTranslation("PLACEHOLDER_SELECT_A_USER") || "Select a user..."}
                            rightSection={<IconSearch size={18} color="#009DF0" />}
                            className={styles.nameInput}
                            data={userLabels}
                            value={form.values.userId === 0 ? '' : allUsers.find(u => u.id === form.values.userId)?.username || ''}
                            onChange={(value) => handleUserChange(value ?? '')}
                            error={form.errors.userId}
                            searchable
                        />
                        <Button
                            mt="md"
                            disabled={!form.values.userId || form.values.userId === 0}
                            onClick={(e) => {
                                e.preventDefault();
                                const user = allUsers.find(u => u.id === form.values.userId);
                                if (user && !addedUsers.some(u => u.id === user.id)) {
                                    setAddedUsers([...addedUsers, { id: user.id, username: user.username }]);
                                    form.setFieldValue('userId', 0); // Reset selection
                                    setSelectKey(prev => prev + 1); // Force Select to re-render and clear
                                }
                            }}>
                            {getTranslation("LABEL_ADDRESS_FORM_ADD_USER") || "Add"}
                        </Button>
                    </Group>
                    {addedUsers.length > 0 && (
                        <Paper p="sm" withBorder>
                            <div className={styles.scrollableTable}>
                                <Table highlightOnHover className={styles.userTable}>
                                    <Table.Thead className={styles.stickyHeader}>
                                        <Table.Tr>
                                            <Table.Th className={styles.headerCell}>
                                                {getTranslation("LABEL_ADDRESS_FORM_USERNAME") || "Username"}
                                            </Table.Th>
                                            <Table.Th className={styles.centerCell}>
                                                {getTranslation("LABEL_ADDRESS_FORM_REMOVE") || "Remove"}
                                            </Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {addedUsers
                                        .sort((a, b) => a.username.localeCompare(b.username))
                                        .map(user => (
                                            <Table.Tr key={user.id}>
                                                <Table.Td className={styles.noBorderCell}>{user.username}</Table.Td>
                                                <Table.Td className={styles.centerCell}>
                                                    <Button variant="subtle" color="red" size="xs" px={4} onClick={() => setAddedUsers(addedUsers.filter(u => u.id !== user.id))}>
                                                        <IconX size={18} />
                                                    </Button>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </div>
                        </Paper>
                    )}
                </Stack>

                <Switch
                    label={getTranslation("LABEL_ARCHIVED") || "Archived"}
                    checked={form.values.status === 1}
                    onChange={(event) => form.setFieldValue('status', event.currentTarget.checked ? 1 : 0)}
                    styles={(theme) => ({
                        track: {
                            backgroundColor: form.values.status === 0 ? 'gray' : undefined,
                            borderColor: form.values.status === 0 ? 'gray' : undefined,
                        }
                    })}
                    mt="md"
                />

                <Group justify="flex-end" mt="md">
                    <Button type="submit">{getTranslation("LABEL_ADDRESS_FORM_SUBMIT") || "Submit"}</Button>
                </Group>
            </form>
        </Modal>
    );
}